import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, OnInit, Output, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { AdvanceFilterComponent } from '../advance-filter/advance-filter.component';

import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-loading-indicator';
import { WorkOrderFilterForm } from '../../../form-model/work-order-filter.form.model';
import { MaintenanceOrderStatus } from '../../../models/maintenance-order-status.model';
import { Mode } from '../../../models/mode.model';
import { Shop } from '../../../models/shop.model';
import { debounceTime, distinctUntilChanged, EMPTY, map, Observable, ReplaySubject, takeUntil, timer } from 'rxjs';
import { MaintenanceOrderFilter } from '../../../models/maintenance-order-filter.model';
import { WorkOrderSearchTextForm } from '../../../form-model/work-order-search-text.form.model';
import { AemsButtonGroupComponent } from '../../../shared-components/aems-button-group/aems-button-group.component';
import { environment } from '../../../../environments/environment';
import { MasterDataService } from '../../../services/master-data.service';
import { ButtonWithConfirmationComponent } from '../../../shared-components/button-with-confirmation/button-with-confirmation.component';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { NotificationService } from '../../../services/notification.service';
import { ToastrService } from '../../../services/toastr-service';
import { APICallStatus } from '../../../models/api-call-status.mode';
import { BulkWorkOrderCompletionBody } from '../../../models/bulk-work-order/bulk-work-order-completion.model';
import { BulkWorkOrderService } from '../../../services/bulk-work-order.service';
import { WorkOrderService } from '../../../services/work-order.service';
import { catchError } from 'rxjs';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { SharedDataService } from '../../../services/shared-data.service';
import { utils } from '../../../helper/utils';
type FilterValue = string | string[] | boolean | Mode[] | MaintenanceOrderStatus[] | Shop[];

@Component({
  selector: 'app-filters',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    AdvanceFilterComponent,
    AemsButtonGroupComponent,
    ButtonWithConfirmationComponent,
    TagWithOverflowComponent,
  ],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class FiltersComponent implements OnInit, AfterViewInit {
  bulkWorkOrderEnabled = signal(environment.Features.EnableBulkWorkOrder);
  showAdvanceFilters: boolean = false;
  canUserCreateWO: boolean = false;
  taggedStatusList: MaintenanceOrderStatus[] = [];
  untaggedStatusList: MaintenanceOrderStatus[] = [];
  readonly screenWidth = window.innerWidth;
  @Input({ required: true }) filterFormGroup!: FormGroup<{
    workOrderFilterForm: FormGroup<WorkOrderFilterForm>;
    workOrderSearchTextForm: FormGroup<WorkOrderSearchTextForm>;
  }>;
  @Input() selectedRows: BulkWorkOrderCompletionBody[] = [];
  savedFilter!: FormGroup<WorkOrderFilterForm>;
  taggedFilters: { key: string; value: FilterValue }[] = [];
  //isMarkAsCompleteDisabled: boolean = false;
  markCompleteApiCallStatus: APICallStatus = 'NOT_STARTED';
  @Output() markAsCompleteSuccess = new EventEmitter<boolean>();
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  @Input() isWorkOrderList: boolean = true;
  advancedFilterLoading = false;
  constructor(
    public authPolicies: AuthPolicies,
    private masterDataService: MasterDataService,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private bulkWorkOrderService: BulkWorkOrderService,
    private sharedDataService: SharedDataService,
    private notificationService: NotificationService,
    public applicationTextService: ApplicationtTextService,
  ) {}
  ngAfterViewInit(): void {
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  ngOnInit() {
    this.masterDataService
      .getMaintenanceOrderStatus()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((statuscount) => {
        this.untaggedStatusList = statuscount;
      });
    if (this.authPolicies.canCreateWorkOrder) {
      this.canUserCreateWO = true;
    }
  }
  private setFilterFormValue() {
    const filterValue = this.sharedDataService.getWorkOrderFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.workOrderFilterForm.setValue(filterValue);
      this.updateSelectedFiltersSection();
    }
  }
  private subscribeToFormChanges() {
    this.filterFormGroup.controls.workOrderFilterForm?.controls.applyWOFilter?.valueChanges.subscribe((value) => {
      if (value) {
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.sharedDataService.setWorkOrderFilterFormValue(this.filterFormGroup.controls.workOrderFilterForm.getRawValue());
      } else {
        this.sharedDataService.setWorkOrderFilterFormValue(null);
      }
    });
    this.filterFormGroup.controls.workOrderSearchTextForm.controls.appliedWOSearchTextFilter?.valueChanges.subscribe((_) => {
      this.updateSelectedFiltersSection();
    });
  }
  get isRowSelectedLimitExceeded(): boolean {
    return this.selectedRows.length > 200;
  }
  get isAnyRowNotApproved(): boolean {
    return this.selectedRows.some((row) => row.statusCode !== 390);
  }
  get isMarkAsCompleteValid(): boolean {
    return this.isAnyRowNotApproved || this.isRowSelectedLimitExceeded;
  }
  markAsCompleteValid(): () => Observable<any> {
    return () => {
      //this.isMarkAsCompleteDisabled = false;
      this.markCompleteApiCallStatus = 'IN_PROGRESS';
      const workOrderIds = this.selectedRows.map((row) => row.workOrderId.toString());
      return this.bulkWorkOrderService.postBulkWorkOrderCompletion(workOrderIds).pipe(
        catchError((error) => {
          this.markCompleteApiCallStatus = 'FAILED';
          this.toastrService.showToastr(
            'We are unable to mark the selected work orders complete due to a technical problem. Please try again later.',
            'error',
            {
              heading: 'Bulk Completion Failed',
            },
          );
          console.log('Error marking work orders as complete:', error);
          return EMPTY; // Return an empty observable to complete the stream
        }),
        map((response) => {
          //All work orders marked as complete
          const heading = `Workorders submitted to be marked as complete`;
          const successMessage = `Workorders have been submitted to be marked as complete. Progress can be tracked in the notifications panel.`;
          this.toastrService.showToastr(successMessage, 'success', { heading: heading });
          this.markCompleteApiCallStatus = 'COMPLETED_SUCCESFULLY';
          this.notificationService.addNotification({
            id: response.id,
            state: 1,
            title: "'Repair Complete' workorders in progress",
            associatedTaskCreatedAtDateRelative: utils.getDateInRelativeFormat(new Date(response.createdAt)),
            associateTaskCreatedAtTime: new Date(response.createdAt).toLocaleTimeString(),
            appearance: 'info',
            icon: 'info-circle',
            daysBefore: 0,
            associatedTaskCreatedAt: new Date(response.createdAt),
            notificationType: 'COMPLETE_WO',
          });
          this.markAsCompleteSuccess.emit(true);
          return response;
        }),
      );
    };
  }
  markAsCompleteInvalid(): void {
    if (this.isRowSelectedLimitExceeded) {
      this.toastrService.showToastr('Please select a maximum of 200 workorders only for Bulk Completion', 'error', {
        heading: 'Bulk Completion Failed',
      });
    }
    if (this.isAnyRowNotApproved) {
      this.toastrService.showToastr('Please select only Approved workorders for Bulk Completion', 'error', {
        heading: 'Bulk Completion Failed',
      });
    }
  }

  selectStatusFromQuickFilter(statusCount: MaintenanceOrderStatus): void {
    const newStatus: MaintenanceOrderStatus = {
      generatedCode: statusCount.generatedCode,
      description: statusCount.description,
    };

    const currentStatuses = this.filterFormGroup.controls.workOrderFilterForm.controls.status.value || [];
    if (!currentStatuses.some((status) => status.generatedCode === newStatus.generatedCode)) {
      const updatedStatuses = [...currentStatuses, newStatus];
      this.filterFormGroup.controls.workOrderFilterForm.controls.status.setValue(updatedStatuses);
      this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
      this.updateSelectedFiltersSection();
    }
  }

  unselectWorkorderNumber(): (_: string) => void {
    return (workorderNumber: string) => {
      const workorderNumberControl = this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber;
      const currentValues = this.taggedFilters.find((x) => x.key == 'WO Number')?.value as string[];
      const updatedValue = currentValues.filter((wonumber) => wonumber != workorderNumber);
      const key = this.taggedFilters.find((x) => x.key == 'WO Number');
      if (key) {
        key.value = updatedValue;
      }
      workorderNumberControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectEquipmentNumber(): (_: string) => void {
    return (equipmentNumber: string) => {
      let key = this.taggedFilters.find((x) => x.key == 'Equipment Id');
      const equipmentNumberControl = this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId;
      const currentValues = key?.value as string[];
      const updatedValue = currentValues.filter((eqnumber) => eqnumber != equipmentNumber);
      if (key) {
        key.value = updatedValue;
      }
      equipmentNumberControl.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }
  unselectSpecificFilter(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1, itemValue = ''): void {
    switch (taggedFilter.key) {
      case 'Equipment Id':
        this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.setValue(null);
        break;

      case 'Mode':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let taggedModes = taggedFilter.value as Mode[];
          const updatedModes: Mode[] = taggedModes.filter((_: Mode, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.workOrderFilterForm.controls.mode.setValue(updatedModes.length ? updatedModes : null);
        }
        break;

      case 'WO Number':
        const woNumbers = this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value as string[];
        const updatedWoNumbers = woNumbers.filter((woNumber) => woNumber !== itemValue);
        this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.setValue(updatedWoNumbers.length ? updatedWoNumbers : null);
        break;

      case 'Status':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let statuses = taggedFilter.value as MaintenanceOrderStatus[];
          const updatedStatuses: MaintenanceOrderStatus[] = statuses.filter(
            (_: MaintenanceOrderStatus, index: number) => index !== itemNumber,
          );
          this.filterFormGroup.controls.workOrderFilterForm.controls.status.setValue(updatedStatuses.length ? updatedStatuses : null);
        }
        break;

      case 'From':
        this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.setValue(null);
        break;

      case 'To':
        this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.setValue(null);
        break;

      case 'Shop ID':
        if (Array.isArray(taggedFilter.value) && itemNumber >= 0 && itemNumber < taggedFilter.value.length) {
          let shops = taggedFilter.value as string[];
          const updatedShops: string[] = shops.filter((_: string, index: number) => index !== itemNumber);
          this.filterFormGroup.controls.workOrderFilterForm.controls.shop.setValue(updatedShops.length ? updatedShops : null);
        }
        break;

      case 'Show only flagged work orders':
        this.filterFormGroup.controls.workOrderFilterForm.controls.showFlaggedWO.setValue(null);
        break;

      default:
        break;
    }
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  showAdvanceFiltersModal() {
    this.advancedFilterLoading = true;
    this.showAdvanceFilters = true;
    this.saveCurrentFilters();
  }
  advancedFilterHasOpened() {
    this.advancedFilterLoading = false;
  }
  closeAdvanceFiltersModal() {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      this.filterFormGroup.controls.workOrderFilterForm.reset(this.savedFilter.value);
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters() {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.setValue(true);
    this.updateSelectedFiltersSection();
  }

  private saveCurrentFilters(): void {
    this.savedFilter = this.formBuilder.group({
      equipmentId: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value),
      mode: this.formBuilder.control<Mode[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value),
      woNumber: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value),
      status: this.formBuilder.control<MaintenanceOrderStatus[] | null>(
        this.filterFormGroup.controls.workOrderFilterForm.controls.status.value,
      ),
      fromDate: this.formBuilder.control<string | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value),
      toDate: this.formBuilder.control<string | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value),
      shop: this.formBuilder.control<string[] | null>(this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value),
      showFlaggedWO: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.workOrderFilterForm.controls.showFlaggedWO.value,
      ),
      applyWOFilter: this.formBuilder.control<boolean | null>(
        this.filterFormGroup.controls.workOrderFilterForm.controls.applyWOFilter.value,
      ),
    });
  }

  updateSelectedFiltersSection() {
    this.markAsTagged('Equipment Id', this.filterFormGroup.controls.workOrderFilterForm.controls.equipmentId.value);
    this.markAsTagged('Mode', this.filterFormGroup.controls.workOrderFilterForm.controls.mode.value);
    this.markAsTagged('WO Number', this.filterFormGroup.controls.workOrderFilterForm.controls.woNumber.value);
    this.markAsTagged('From', this.filterFormGroup.controls.workOrderFilterForm.controls.fromDate.value);
    this.markAsTagged('To', this.filterFormGroup.controls.workOrderFilterForm.controls.toDate.value);
    this.markAsTagged('Shop ID', this.filterFormGroup.controls.workOrderFilterForm.controls.shop.value);
    this.markAsTagged('Show only flagged work orders', this.filterFormGroup.controls.workOrderFilterForm.controls.showFlaggedWO.value);
    this.updateStatusTag();
  }

  private updateStatusTag() {
    this.masterDataService
      .getMaintenanceOrderStatus()
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const selectedStatuses = this.filterFormGroup.controls.workOrderFilterForm.controls.status.value || [];

          this.taggedStatusList = response.filter((status) =>
            selectedStatuses.some((selected) => selected.generatedCode === status.generatedCode),
          );
          this.untaggedStatusList = response.filter(
            (status) => !selectedStatuses.some((selected) => selected.generatedCode === status.generatedCode),
          );
          this.markAsTagged('Status', this.filterFormGroup.controls.workOrderFilterForm.controls.status.value);
        },
        error: (err) => {},
      });
  }

  markAsTagged(key: string, newValue: FilterValue | null) {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== false && newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string) {
    this.taggedFilters = this.taggedFilters.filter((filter) => filter.key !== key);
  }

  getFilterValue(key: string) {
    const filter = this.taggedFilters.find((filter) => filter.key === key);
    return filter ? filter.value : null;
  }

  getTaggedFilterValue(taggedFilter: { key: string; value: FilterValue }, itemNumber: number = -1): FilterValue {
    switch (taggedFilter.key) {
      case 'From':
      case 'To':
        return taggedFilter.value;

      case 'Mode':
        let modes = taggedFilter.value as Mode[];
        return modes[itemNumber].code;

      case 'Status':
        let statuses = taggedFilter.value as MaintenanceOrderStatus[];
        return `${statuses[itemNumber].description}`;

      case 'Shop ID':
        let shops = taggedFilter.value as string[];
        return shops[itemNumber];

      case 'Show only flagged work orders':
        let showFlaggedWO = taggedFilter.value as boolean;
        return showFlaggedWO ? 'Yes' : 'No';

      default:
        return '';
    }
  }

  getTaggedWorkOrderNumbers(): string[] {
    return (this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'WO Number')?.value as string[]) || [];
  }
  getTaggedEquipmentNumbers(): string[] {
    return (this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Equipment Id')?.value as string[]) || [];
  }

  getTaggedModes(): Mode[] {
    const modeTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Mode');
    return modeTag && Array.isArray(modeTag.value) ? (modeTag.value as Mode[]) : [];
  }

  getTaggedStatuses(): MaintenanceOrderStatus[] {
    const statusTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Status');
    return statusTag && Array.isArray(statusTag.value) ? (statusTag.value as MaintenanceOrderStatus[]) : [];
  }

  getTaggedShops(): string[] {
    const shopTag = this.taggedFilters.find((taggedFilter) => taggedFilter.key === 'Shop ID');
    return shopTag && Array.isArray(shopTag.value) ? (shopTag.value as string[]) : [];
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  get numberOfQuickFiltersToShow(): number {
    if (this.screenWidth >= 320 && this.screenWidth < 768) {
      return 2;
    }
    if (this.screenWidth >= 768 && this.screenWidth < 1024) {
      return 3;
    }
    if (this.screenWidth >= 1024 && this.screenWidth < 1280) {
      return 4;
    }
    if (this.screenWidth >= 1280 && this.screenWidth < 1920) {
      return 6;
    }
    if (this.screenWidth >= 1920 && this.screenWidth < 2560) {
      return 8;
    }
    if (this.screenWidth >= 2560 && this.screenWidth < 3840) {
      return 12;
    }
    return 100;
  }
}
